import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../../../../../interceptor/axiosInstance';
import './OptionEditTable.css';

// Toast notification functions
const toast = {
  success: (message) => {
    const toastEl = document.createElement('div');
    toastEl.className = 'edit-option-toast edit-option-toast-success';
    toastEl.textContent = message;
    document.body.appendChild(toastEl);
    setTimeout(() => toastEl.classList.add('edit-option-toast-show'), 10);
    setTimeout(() => {
      toastEl.classList.remove('edit-option-toast-show');
      setTimeout(() => document.body.removeChild(toastEl), 300);
    }, 3000);
  },
  error: (message) => {
    const toastEl = document.createElement('div');
    toastEl.className = 'edit-option-toast edit-option-toast-error';
    toastEl.textContent = message;
    document.body.appendChild(toastEl);
    setTimeout(() => toastEl.classList.add('edit-option-toast-show'), 10);
    setTimeout(() => {
      toastEl.classList.remove('edit-option-toast-show');
      setTimeout(() => document.body.removeChild(toastEl), 300);
    }, 3000);
  },
};

const OptionEditTable = ({ optionId, onClose, onSaveSuccess }) => {
  const [option, setOption] = useState(null);
  const [saving, setSaving] = useState(false);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOption = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`assessments/options/${optionId}/`);
        const data = response.data.data;

        setOption({
          id: data.id,
          text: data.text || '',
          is_correct: data.is_correct || false,
          preview: data.image ? `${axiosInstance.defaults.baseURL}${data.image}` : null,
          image: null,
        });

      } catch (error) {
        console.error('Error loading option:', error);
        toast.error('Failed to load option data');
      } finally {
        setLoading(false);
      }
    };
    fetchOption();
  }, [optionId]);

  const handleOptionChange = (key, value) => {
    setOption((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageChange = (file) => {
    if (!file) return;
    setOption((prev) => ({
      ...prev,
      image: file,
      preview: URL.createObjectURL(file),
    }));
  };

  const handleSave = async () => {
    if (!option.text.trim()) {
      toast.error('Option text is required');
      return;
    }

    setSaving(true);
    try {
      const formDataOption = new FormData();
      formDataOption.append('text', option.text);
      formDataOption.append('is_correct', option.is_correct);
      if (option.image instanceof File) {
        formDataOption.append('image', option.image);
      }

      await axiosInstance.put(`assessments/options/${option.id}/`, formDataOption, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Option updated successfully!');
      onSaveSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const modalContent = (
    <div className="edit-option-modal-overlay" onClick={onClose}>
      <div className="edit-option-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="edit-option-modal-header">
          <h2 className="edit-option-modal-title">✏️ Edit Option</h2>
          <button className="edit-option-modal-close" onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="edit-option-modal-body">
          {loading ? (
            <div className="edit-option-loading">
              <div className="edit-option-spinner"></div>
              <p>Loading option data...</p>
            </div>
          ) : (
            <>
              {/* Option Text */}
              <div className="edit-option-form-group">
                <label className="edit-option-label">
                  Option Text <span className="edit-option-required">*</span>
                </label>
                <textarea
                  className="edit-option-textarea"
                  value={option.text ?? ''}
                  onChange={(e) => handleOptionChange('text', e.target.value)}
                  rows={3}
                  placeholder="Enter option text..."
                />
              </div>

              {/* Correct Checkbox */}
              <div className="edit-option-form-group">
                <label className="edit-option-checkbox-label">
                  <input
                    type="checkbox"
                    className="edit-option-checkbox"
                    checked={option.is_correct}
                    onChange={(e) => handleOptionChange('is_correct', e.target.checked)}
                  />
                  <span className="edit-option-checkbox-text">Mark as Correct Answer</span>
                </label>
              </div>

              {/* Image Upload */}
              <div className="edit-option-form-group">
                <label className="edit-option-label">Option Image (Optional)</label>
                {option.preview && (
                  <div className="edit-option-image-preview-wrapper">
                    <img src={option.preview} alt="Preview" className="edit-option-image-preview" />
                  </div>
                )}
                <input
                  type="file"
                  id="edit-option-image-upload"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handleImageChange(e.target.files[0])}
                />
                <button
                  type="button"
                  className="edit-option-upload-btn"
                  onClick={() => document.getElementById('edit-option-image-upload').click()}
                >
                  📷 {option.preview ? 'Change Image' : 'Upload Image'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="edit-option-modal-footer">
          <button className="edit-option-cancel-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="edit-option-save-btn"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <span className="edit-option-btn-spinner"></span>
                Saving...
              </>
            ) : (
              '💾 Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default OptionEditTable;
